import axios from 'axios';
import { config as appConfig } from '../config/environment';

/**
 * This browser's side of push notifications: asking permission, subscribing with the push
 * service, and undoing both. The server's side -- who a subscription belongs to -- is
 * `services.push`; nothing here talks to the API except `signOutOfPush`, which cannot use it.
 *
 * **A subscription is the browser's, not the account's.** It lives in the browser, outlives a
 * session, and is handed to whoever signs in next (`usePushSync`). Signing out ends it.
 *
 * **The service worker is registered here and only here**, the first time somebody turns
 * notifications on. A visitor who never does never gets one.
 */

const WORKER_URL = '/sw.js';

/** Thrown by `enablePush` when the person, or their browser's settings, said no. */
export class PushPermissionDenied extends Error {
    constructor() {
        super('Notification permission was not granted.');
        this.name = 'PushPermissionDenied';
    }
}

export const pushSupported = (): boolean =>
    typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && 'PushManager' in window
    && 'Notification' in window;

/**
 * An iPhone or iPad in a Safari tab. Push exists there only for a site added to the Home
 * Screen and opened from it, so the browser reports no support at all -- and the right thing
 * to show is how to get it, not nothing. An iPad calls itself a Mac; the touch points give it
 * away.
 */
export const needsHomeScreen = (): boolean => {
    if (typeof navigator === 'undefined' || pushSupported()) {
        return false;
    }

    return /iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/** Whether the person has refused, which only the browser's own settings can undo. */
export const pushBlocked = (): boolean => pushSupported() && Notification.permission === 'denied';

/** `applicationServerKey` takes bytes; the server sends the key as base64url. */
export const keyToBytes = (base64Url: string): Uint8Array => {
    const padded: string = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=');
    const raw: string = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

    return Uint8Array.from(raw, (character: string) => character.charCodeAt(0));
};

/** The subscription this browser already has, if any. Never registers anything. */
export const currentPushSubscription = async (): Promise<PushSubscription | null> => {
    if (!pushSupported() || Notification.permission !== 'granted') {
        return null;
    }

    const registration = await navigator.serviceWorker.getRegistration(WORKER_URL);

    return (await registration?.pushManager.getSubscription()) ?? null;
};

/**
 * Asks, registers the worker and subscribes. Call it from a click: an iPhone only shows the
 * permission prompt in direct answer to one, which is why asking is the first thing awaited.
 */
export const enablePush = async (publicKey: string): Promise<PushSubscription> => {
    if ((await Notification.requestPermission()) !== 'granted') {
        throw new PushPermissionDenied();
    }

    const registration: ServiceWorkerRegistration = await navigator.serviceWorker.register(WORKER_URL);
    await navigator.serviceWorker.ready;

    const subscribe = (): Promise<PushSubscription> => registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyToBytes(publicKey),
    });

    try {
        return await subscribe();
    } catch (error) {
        // A subscription made with another key is refused rather than replaced -- which is
        // what a browser holds after the server's key pair has changed. Drop it and ask again.
        const stale: PushSubscription | null = await registration.pushManager.getSubscription();

        if (!stale) {
            throw error;
        }

        await stale.unsubscribe();

        return subscribe();
    }
};

/** Unsubscribes this browser and returns the endpoint it had, for the server to forget. */
export const disablePush = async (): Promise<string | null> => {
    const subscription: PushSubscription | null = await currentPushSubscription();

    if (!subscription) {
        return null;
    }

    await subscription.unsubscribe();

    return subscription.endpoint;
};

/**
 * Signing out: this browser stops being told, here and on the server.
 *
 * Takes the token rather than reading it, and goes round the API layer, because sign-out
 * clears the stored session in the same tick -- by the time the subscription has been looked
 * up there is no token left for an interceptor to attach. Fire and forget: a failure here
 * must not hold up, or undo, signing out.
 */
export const signOutOfPush = (token: string | null): void => {
    if (!token || !pushSupported()) {
        return;
    }

    void disablePush()
        .then((endpoint: string | null) => endpoint === null
            ? undefined
            : axios.delete(`${appConfig.apiBaseUrl}/push/subscription`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { endpoint },
            }))
        .catch(() => undefined);
};
